import { useContext, useEffect } from 'react'
import ErrorScreen from '../../Misc/ErrorScreen'
import {
  Button,
  Grid,
  Link as LinkMui,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import { receivePaymentType } from '../../types'
import { Link as LinkRouter } from 'react-router-dom'
import dayjs from 'dayjs'
import { dataContext } from '../..'
import { DatePicker } from '../../helper'

function PaymentList(props: {
  paymentList: (receivePaymentType & {
    customer_name: string
    job_name: string
  })[]
  paymentCount: number
  filters: { [index: string]: string | number }
  searchError: boolean
  initialLoad: boolean
  listLoading: boolean
  handleSearch: (filters: { [index: string]: string | number }) => void
  setFilters: (filters: { [index: string]: string | number }) => void
}) {
  const { user } = useContext(dataContext)

  useEffect(() => {
    if (user.active_flag) {
      props.handleSearch(props.filters)
    }
  }, [user])

  if (props.searchError) {
    return (
      <div className="customer-list-error-container">
        <ErrorScreen
          errorText="Something went wrong while searching. Please try again or contact support if the error persists."
          title="Search Failed"
        />
      </div>
    )
  }

  return (
    <TableContainer className="customer-list-table">
      <div className="customer-pagination-header">
        <div className="customer-list-title">
          {props.initialLoad && props.listLoading ? (
            <Skeleton width={100} height={32} />
          ) : (
            <Typography variant="h6">Payments</Typography>
          )}
        </div>
        {props.initialLoad && props.listLoading ? (
          <Skeleton width={300} height={32} />
        ) : (
          <TablePagination
            component="div"
            count={props.paymentCount}
            page={props.filters.page as number}
            rowsPerPage={props.filters.limit as number}
            onPageChange={(_e, newPage) => {
              const newFilters = { ...props.filters, page: newPage }
              props.setFilters(newFilters)
              props.handleSearch(newFilters)
            }}
            onRowsPerPageChange={e => {
              const newFilters = { ...props.filters, limit: parseInt(e.target.value) }
              props.setFilters(newFilters)
              props.handleSearch(newFilters)
            }}
          />
        )}
      </div>
      <Table stickyHeader color="primary">
        <TableHead>
          {props.initialLoad && props.listLoading ? (
            <TableRow>
              {Array.from({ length: 7 }).map((_, index) => (
                <TableCell key={index}>
                  <Skeleton className="skeleton-header" />
                </TableCell>
              ))}
            </TableRow>
          ) : (
            <TableRow>
              <TableCell width="14%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('ref_number')}
                  direction={props.filters.sort === 'ref_number' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'ref_number' ? 'ref_number desc' : 'ref_number',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Payment Reference #
                </TableSortLabel>
              </TableCell>
              <TableCell width="14%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('customer_name')}
                  direction={props.filters.sort === 'customer_name' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'customer_name' ? 'customer_name desc' : 'customer_name',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Customer Name
                </TableSortLabel>
              </TableCell>
              <TableCell width="14%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('job_name')}
                  direction={props.filters.sort === 'job_name' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'job_name' ? 'job_name desc' : 'job_name',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Job Name
                </TableSortLabel>
              </TableCell>
              <TableCell width="14%" align="right">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('invoice_ref_number')}
                  direction={props.filters.sort === 'invoice_ref_number' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort:
                        props.filters.sort === 'invoice_ref_number' ? 'invoice_ref_number desc' : 'invoice_ref_number',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Invoice #
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" width="14%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('txn_date')}
                  direction={props.filters.sort === 'txn_date' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'txn_date' ? 'txn_date desc' : 'txn_date',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Transaction Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" width="14%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('total_amount')}
                  direction={props.filters.sort === 'total_amount' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'total_amount' ? 'total_amount desc' : 'total_amount',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Total Amount
                </TableSortLabel>
              </TableCell>
              <TableCell width="14%">
                <TableSortLabel
                  active={(props.filters.sort as string).includes('payment_method')}
                  direction={props.filters.sort === 'payment_method' ? 'asc' : 'desc'}
                  onClick={() => {
                    const newFilters = {
                      ...props.filters,
                      sort: props.filters.sort === 'payment_method' ? 'payment_method desc' : 'payment_method',
                    }
                    props.setFilters(newFilters)
                    props.handleSearch(newFilters)
                  }}
                >
                  Payment Method
                </TableSortLabel>
              </TableCell>
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {props.listLoading
            ? Array.from({ length: props.filters.limit as number }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 7 }).map((_, index) => (
                    <TableCell key={index}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : props.paymentList.map(payment => (
                <TableRow key={payment.receive_payment_txn_id}>
                  <TableCell>
                    <LinkMui to={`/customer/payment/${payment.receive_payment_txn_id}`} component={LinkRouter}>
                      {payment.ref_number ?? '-'}
                    </LinkMui>
                  </TableCell>
                  <TableCell>{payment.customer_name ? payment.customer_name : '-'}</TableCell>
                  <TableCell>{payment.job_name ? payment.job_name : '-'}</TableCell>
                  <TableCell align="right">{payment.invoice_ref_number ? payment.invoice_ref_number : '-'}</TableCell>
                  <TableCell align="right">
                    {payment.txn_date ? dayjs.utc(payment.txn_date).format('MM/DD/YYYY') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {payment.total_amount
                      ? payment.total_amount.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>{payment.payment_method ? payment.payment_method : '-'}</TableCell>
                </TableRow>
              ))}
        </TableBody>
      </Table>
      <TablePagination
        className="customer-pagination-footer"
        component="div"
        count={props.paymentCount}
        page={props.filters.page as number}
        rowsPerPage={props.filters.limit as number}
        onPageChange={(_e, newPage) => {
          const newFilters = { ...props.filters, page: newPage }
          props.setFilters(newFilters)
          props.handleSearch(newFilters)
        }}
        onRowsPerPageChange={e => {
          const newFilters = { ...props.filters, limit: parseInt(e.target.value) }
          props.setFilters(newFilters)
          props.handleSearch(newFilters)
        }}
      />
    </TableContainer>
  )
}

export function PaymentSearch(props: {
  initialLoad: boolean
  listLoading: boolean
  filters: { [index: string]: string | number }
  setFilters: (filters: { [index: string]: string | number }) => void
  handleSearch: (filters: { [index: string]: string | number }) => void
}) {
  const handleClear = () => {
    const newFilters = {
      page: 0,
      limit: 25,
      sort: 'txn_date desc',
      paymentNumber: '',
      customerPoNumber: '',
      customerJobName: '',
      fromDate: '',
      toDate: '',
    }

    props.setFilters(newFilters)
    props.handleSearch(newFilters)
  }

  if (props.initialLoad && props.listLoading) {
    return (
      <form className="customer-advanced-search-form">
        <Grid container spacing={'17px'}>
          {Array.from({ length: 4 }).map((_, idx) => (
            <Grid size={3} className="customer-advanced-search-grid" key={idx}>
              <Skeleton width={120} height={24} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={'17px'}>
          {Array.from({ length: 4 }).map((_, idx) => (
            <Grid size={3} className="customer-advanced-search-grid" key={idx}>
              <Skeleton variant="rounded" width="100%" height={40} />
            </Grid>
          ))}
        </Grid>
        <div className="customer-advanced-search-buttons">
          <Button
            size="large"
            variant="outlined"
            onClick={e => {
              e.preventDefault()
              handleClear()
            }}
            loading={props.listLoading}
          >
            Clear
          </Button>
          <Button
            size="large"
            variant="contained"
            type="submit"
            onClick={e => {
              e.preventDefault()
              props.handleSearch(props.filters)
            }}
            loading={props.listLoading}
          >
            Search
          </Button>
        </div>
      </form>
    )
  }
  return (
    <form className="customer-advanced-search-form">
      <Grid container spacing={'17px'}>
        <Grid size={3} className="customer-advanced-search-grid">
          <Typography variant="body2">Payment Number:</Typography>
        </Grid>
        <Grid size={3} className="customer-advanced-search-grid">
          <Typography variant="body2">Customer PO Number:</Typography>
        </Grid>
        <Grid size={3} className="customer-advanced-search-grid">
          <Typography variant="body2">Customer Job Name:</Typography>
        </Grid>
        <Grid size={3} className="customer-advanced-search-grid">
          <Typography variant="body2">Credit Date:</Typography>
        </Grid>
      </Grid>
      <Grid container spacing={'17px'}>
        <Grid size={3} className="customer-advanced-search-grid">
          <TextField
            variant="outlined"
            size="small"
            label="Payment Number"
            value={props.filters.paymentNumber}
            fullWidth
            onChange={e => {
              props.setFilters({
                ...props.filters,
                paymentNumber: e.target.value,
              })
            }}
          />
        </Grid>
        <Grid size={3} className="customer-advanced-search-grid">
          <TextField
            variant="outlined"
            size="small"
            label="Customer PO Number"
            value={props.filters.customerPoNumber}
            fullWidth
            onChange={e => {
              props.setFilters({
                ...props.filters,
                customerPoNumber: e.target.value,
              })
            }}
          />
        </Grid>
        <Grid size={3} className="customer-advanced-search-grid">
          <TextField
            variant="outlined"
            size="small"
            label="Customer Job Name"
            value={props.filters.customerJobName}
            fullWidth
            onChange={e => {
              props.setFilters({
                ...props.filters,
                customerJobName: e.target.value,
              })
            }}
          />
        </Grid>
        <Grid size={3} className="customer-advanced-search-grid">
          <div className="customer-advanced-search-date-group">
            <DatePicker
              size="small"
              label="From Date"
              value={props.filters.fromDate ? dayjs.utc(props.filters.fromDate) : null}
              onChange={e => {
                props.setFilters({
                  ...props.filters,
                  fromDate: e?.format('YYYY-MM-DD') ?? '',
                })
              }}
            />
            <DatePicker
              size="small"
              label="To Date"
              value={props.filters.toDate ? dayjs.utc(props.filters.toDate) : null}
              onChange={e => {
                props.setFilters({
                  ...props.filters,
                  toDate: e?.format('YYYY-MM-DD') ?? '',
                })
              }}
            />
          </div>
        </Grid>
      </Grid>
      <div className="customer-advanced-search-buttons">
        <Button
          size="large"
          variant="outlined"
          onClick={e => {
            e.preventDefault()
            handleClear()
          }}
          loading={props.listLoading && props.initialLoad}
        >
          Clear
        </Button>
        <Button
          size="large"
          variant="contained"
          type="submit"
          onClick={e => {
            e.preventDefault()
            props.handleSearch(props.filters)
          }}
          loading={props.listLoading && props.initialLoad}
        >
          Search
        </Button>
      </div>
    </form>
  )
}

export default PaymentList
